import { CSSProperties, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import dayjs from "dayjs";
import millify from "millify";

// Components
import UpgradeToProSucceededModal from "components/shareable/UpgradeToProSucceededModal/UpgradeToProSucceededModal";
import ButtonSymphony from "elements/ButtonSymphony";
import Recommendations from "components/shareable/Recommendations";
import WelcomeToSymphony from "components/WelcomeToSymphony";
import ModalSymphony from "elements/ModalSymphony";
import PageSymphony from "elements/PageSymphony";
import ContentSymphony from "components/shareable/ContentSymphony";
import DefaultLearningIndicator, { LearningIndicatorType } from "components/shareable/LearningIndicator/DefaultLearningIndicator";
import CardSymphony from "elements/CardSymphony";
import SmallGraph from "components/dashboard/SmallGraph";

// Hooks
import useCurrentBrand, { BrandExistsState } from 'Hooks/useCurrentBrand';
import useCurrentUser, { UserExistsState } from 'Hooks/useCurrentUser';
import useScreen from "Hooks/useScreen";
import { GraphPresentation, useDashboard } from "Hooks/useDashboard";

// Utils & Constants
import { track } from "analytics";
import { generateShortUniqueId, getCampaignTypeInfo, getFallbackCampaignName, getStatusToShowPaymentFailedBanner, makeKey, styx } from "modules/Utils";
import Colors from "modules/Colors";
import { AdPlatform, CAMPAIGN_STATUS_INFO, CampaignStatus, PlaybookType, SocialConnectType, WebsiteTemplateCategory } from "modules/Const";
import { setCookie } from "modules/Utils";

// Assets
import chevronRightGray from 'assets/images/chevronRight-gray.svg';

// Types
import { CurrentBrand, SpotifyArtistInBrandConnections } from "modules/Types";

// Styles
import "styles/react-datepicker.css";
import { Fade, SvgIcon } from "@material-ui/core";
import OverallDashboard from "./OverallDashboard";
import PlatformsConnector from "./PlatformsConnector";
import SocialConnectModal from "components/shareable/SocialConnectModal";
import LargeGraphPopup from "./components/LargeGraphPopup";
import { connectToBrand_api } from 'api/brandApi';
import Loader from 'elements/Loader';
import useCampaign from "Hooks/useCampaign";
import Sty from "modules/Sty";
import useWebsiteBuilder from "Hooks/useWebsiteBuilder";
import { useMarketingCampaigns } from "Hooks/useMarketingCampaigns";
import DebugBox from "components/shareable/DebugBox";
import TagSymphony from "elements/TagSymphony";
import gradientFrostedPurple from 'assets/v2/images/gradientFrostedPurple.png';
import { CAMPAIGN_FILTER_DEFAULT } from "pages/MarketingPage/constants";
import CampaignImageSymphony from "elements/CampaignImageSymphony";
import Icon from "elements/Icon";
import { toastSuccess } from "modules/Toast";

const SHOW_ACTIVE_CAMPAIGNS = true;

enum DashboardSideContent {
    INSTAGRAM = 'instagram',
    YOUTUBE = 'youtube',
    FACEBOOK = 'facebook',
    TIKTOK = 'tiktok',
    SOUNDCLOUD = 'soundcloud',
    DEEZER = 'deezer',
}

interface DashboardSideContentItem {
    title: string;
    description: string;
    image: string;
}

const dashboardSideContent: { [key in DashboardSideContent]: DashboardSideContentItem } = {
    [DashboardSideContent.INSTAGRAM]: {
        title: 'Instagram Post Breaks Like Record',
        description: 'Your latest Instagram post has reached a new milestone with record-breaking engagement. Check out the analytics to see what resonated with your audience.',
        image: 'https://picsum.photos/200/300',
    },
    [DashboardSideContent.YOUTUBE]: {
        title: 'Promote the Light with your new AI generated Website',
        description: 'Launch your new AI-powered website to showcase your music and connect with fans. Customize your site with just a few clicks.',
        image: 'https://picsum.photos/200/300',
    },
    [DashboardSideContent.FACEBOOK]: {
        title: 'Los Angeles seems to be growing more than usual',
        description: 'Your fanbase in Los Angeles has grown significantly this month. Consider planning special content or events to engage with this growing audience.',
        image: 'https://picsum.photos/200/300',
    },
    [DashboardSideContent.TIKTOK]: {
        title: 'TikTok Content',
        description: 'Explore trending sounds and hashtags to boost your TikTok presence. Create engaging short-form videos to reach new audiences.',
        image: 'https://picsum.photos/200/300',
    },
    [DashboardSideContent.SOUNDCLOUD]: {
        title: 'SoundCloud Content',
        description: 'Share your latest tracks and connect with the SoundCloud community. Monitor your track performance and engage with listeners.',
        image: 'https://picsum.photos/200/300',
    },
    [DashboardSideContent.DEEZER]: {
        title: 'Deezer Content',
        description: 'Optimize your Deezer profile and playlists to increase streams. Track your performance metrics and grow your listener base.',
        image: 'https://picsum.photos/200/300',
    }
}

const dashboardSideContentArray = [
    DashboardSideContent.INSTAGRAM,
    DashboardSideContent.YOUTUBE,
    DashboardSideContent.FACEBOOK,
]

const useQuery = () => {
    return new URLSearchParams(useLocation().search);
}

export interface FanbaseMetricsType {
    totalFanbase: number;
    foreverPresaveFans: number;
    fanbaseGrowth: string;
    foreverPresaveFansGrowth: string;
    dailyMetrics: {
        [key: string]: {
            totalFans: number;
            foreverSaves: number;
        }
    };
}

// TODO: ask megh for icons - music-player by default
export const siteIconsData: { [key: string]: any; } = {
    deezer: require("assets/images/platforms/deezer.svg").default,
    spotify: require("assets/images/platforms/spotify.svg").default,
    tidal: require("assets/images/platforms/tidal.svg").default,
    youtube: require("assets/images/platforms/youtube.svg").default,
    apple_music: require("assets/images/music-player.svg").default,
    amazonStore: require("assets/images/music-player.svg").default,
    amazon_music: require("assets/images/music-player.svg").default,
    instagram: require("assets/images/platforms/instagram.svg").default,
    tiktok: require("assets/images/platforms/tiktok.svg").default,
    facebook: require("assets/images/platforms/facebook.svg").default,
    soundcloud: require("assets/images/platforms/soundcloud.svg").default,
    twitter: require("assets/images/twitter.svg").default,
    audiomack: require("assets/images/platforms/audiomack.svg").default,
    fans: require("assets/images/platforms/fans.svg").default,
    'forever-saves': require("assets/images/platforms/forever-saves.svg").default,
}

const Dashboard = () => {
    const history = useHistory();
    const { smallView, mediumView, mStyle, cssCalc } = useScreen();
    const { currentBrand, isBrandAdmin } = useCurrentBrand() as BrandExistsState
    const { isSymphonyBetaTestUser } = useCurrentUser() as UserExistsState;
    const {
        getDashboardData,
        isLoadingInitialData,
        timeRange,
        selectedDate,
        platformGraphsData,
        selectedPlatformForConnect,
        setSelectedPlatformForConnect,
        isLoadingCampaignsRecent,
        campaignsRecent,
        getCampaignsRecent
    } = useDashboard();

    const {
        setShowCreateNewCampaignModal,
        setNewCampaignAdPlatformFilter
    } = useCampaign();

    const {
        setShowCreateNewWebsiteModalV2,
        setSelectedTemplateCategory,
    } = useWebsiteBuilder();

    const websiteSlug = generateShortUniqueId()

    const commonTrackBrandData = {
        brand_id: currentBrand.id,
        brand_name: currentBrand.name,
        brand_slug: currentBrand.slug,
    }

    const handleNewWebsite = () => {
        setShowCreateNewWebsiteModalV2(true)
        history.push(`/website-builder/${currentBrand?.slug}/${websiteSlug}`)
        setSelectedTemplateCategory(WebsiteTemplateCategory.POPULAR)
    }

    const PLAYBOOKS_FOR_DASHBOARD: {
        name: string;
        icon: string;
        title: string;
        background: string;
        onClick: () => void;
        hidden?: boolean;
        onlyForSymphonyBetaTestUsers?: boolean;
    }[] = [
            {
                name: 'boostInstagramEngagement',
                icon: 'instagram',
                title: 'Run IG Ads',
                background: Colors.gradientInstagram,
                onClick: () => {
                    setShowCreateNewCampaignModal(true)
                    setNewCampaignAdPlatformFilter(AdPlatform.META)
                    const commonTrackData = {
                        adPlatform: AdPlatform.META,
                        playbookType: PlaybookType.BOOST_INSTAGRAM_ENGAGEMENT,
                        ...commonTrackBrandData,
                    }
                    track("Campaign Started", commonTrackData)
                    track("Playbook Card Selection", commonTrackData)
                }
            },
            {
                name: 'runTikTokAds',
                icon: 'tiktok',
                title: 'Run TikTok Ads',
                background: Colors.gradientTikTok,
                onClick: () => {
                    setShowCreateNewCampaignModal(true)
                    setNewCampaignAdPlatformFilter(AdPlatform.TIKTOK)
                    const commonTrackData = {
                        adPlatform: AdPlatform.TIKTOK,
                        playbookType: PlaybookType.TIKTOK_BOOST_ENGAGEMENT,
                        ...commonTrackBrandData,
                    }
                    track("Campaign Started", commonTrackData)
                    track("Playbook Card Selection", commonTrackData)
                }
            },
            {
                name: 'createPreSave',
                icon: 'preSave',
                title: 'Create a Pre-save',
                background: Colors.gradientPresave,
                onClick: () => {
                    const commonTrackData = {
                        playbookType: PlaybookType.META_PRESAVES,
                        ...commonTrackBrandData,
                    }
                    track("Campaign Started", commonTrackData)
                    track("Playbook Card Selection", commonTrackData)

                    history.push('/marketing/new/presave')
                }
            },
            {
                name: 'youtubeAds',
                icon: 'youtubeFlat',
                title: 'Run YouTube Ads',
                hidden: false,
                background: Colors.gradientYouTube,
                onClick: () => {
                    const commonTrackData = {
                        playbookType: PlaybookType.INCREASE_VIDEO_VIEWS,
                        ...commonTrackBrandData,
                    }
                    track("Campaign Started", commonTrackData)
                    track("Playbook Card Selection", commonTrackData)
                    history.push('/marketing/new/video-views')
                }
            },
            {
                name: 'buildWebsite',
                icon: 'musicSite',
                title: 'Build a Website',
                background: Colors.gradientWebsite,
                onClick: handleNewWebsite
            },
        ]

    /** Controls visibility of the welcome modal */
    const [isFirstDashboardDataLoad, setIsFirstDashboardDataLoad] = useState<boolean>(true)
    const [showWelcomeModal, setShowWelcomeModal] = useState<boolean>(false);

    const showPaymentFailedBanner = getStatusToShowPaymentFailedBanner(currentBrand)
    const [sessionId, setSessionId] = useState(String())
    const [showUpgradeToProSucceededModal, setShowUpgradeToProSucceededModal] = useState<boolean>(false)

    // TODO: Set this to true when we have recommendations sidebar ready to go
    const [showRecommendationsSidebar, setShowRecommendationsSidebar] = useState<boolean>(false)

    // edit platforms modal
    const [openAlreadyExistModal, setOpenAlreadyExistModal] = useState<boolean>(false)
    const [brandToSwitchTo, setBrandToSwitchTo] = useState<CurrentBrand | null>(null);
    const [alreadyExistArtist, setAlreadyExistArtist] = useState<SpotifyArtistInBrandConnections | null>(null);
    const [showLargeGraph, setShowLargeGraph] = useState<GraphPresentation | null>(null)
    const [showSocialConnectModal, setShowSocialConnectModal] = useState<boolean>(false)

    /** Controls visibility of the TikTok auth modal */
    const [showTikTokAuthModal, setShowTikTokAuthModal] = useState<boolean>(false);
    const [isTikTokConnecting, setIsTikTokConnecting] = useState<boolean>(false);
    const [tikTokError, setTikTokError] = useState<string | null>(null);

    useEffect(() => {
        if (currentBrand?.dashboard_initialized && selectedDate) {
            track('Updated Time Range', {
                name: 'Dashboard',
                to: timeRange,
                startDate: selectedDate.startDate,
                endDate: selectedDate.endDate,
                brand_id: currentBrand.id,
                brand_slug: currentBrand.slug,
                brand_name: currentBrand.name
            });
        }
    }, [selectedDate, timeRange]);

    const getInitialDashboardAndCampaignData = async () => {
        setIsFirstDashboardDataLoad(true)
        try {
            await getDashboardData({
                startDate: selectedDate.startDate,
                endDate: selectedDate.endDate
            })

            if (!isLoadingCampaignsRecent) {
                await getCampaignsRecent({
                    brandId: currentBrand.id,
                    // isAnalytics: true
                })
            }
        } catch (error) {
            console.error('Error getting initial dashboard and campaign data', error)
        } finally {
            setIsFirstDashboardDataLoad(false)
        }
    }

    useEffect(() => {
        if (currentBrand) {
            getInitialDashboardAndCampaignData()
        }

        const authCode = localStorage.getItem('tt_auth_code');
        if (authCode && currentBrand?.id) {
            setShowTikTokAuthModal(true);
            handleTikTokAuth(authCode);
        }
    }, [currentBrand]);

    const checkSubscriptionStatus = async () => {
        const urlParams = new URLSearchParams(window.location.search)
        const subscription = urlParams.get("subscription")
        const brand = urlParams.get("brand")
        const session_id = urlParams.get("session_id")

        if (
            session_id &&
            subscription === "succeeded" &&
            brand === currentBrand?.slug
        ) {
            setSessionId(session_id)
            setShowUpgradeToProSucceededModal(true)
        } else {
            setSessionId(String())
            setShowUpgradeToProSucceededModal(false)
        }
    }

    const closeUpgradeToProSucceededModal = () => {
        setSessionId(String())
        setShowUpgradeToProSucceededModal(false)
    }

    const handleItemClick = (itemType: DashboardSideContent) => {
        switch (itemType) {
            case DashboardSideContent.INSTAGRAM:
                // TODO: Handle Instagram click
                break;
            case DashboardSideContent.YOUTUBE:
                // TODO: Handle YouTube click
                break;
            case DashboardSideContent.FACEBOOK:
                // TODO: Handle Facebook click
                break;
            case DashboardSideContent.TIKTOK:
                // TODO: Handle TikTok click
                break;
            case DashboardSideContent.SOUNDCLOUD:
                // TODO: Handle SoundCloud click
                break;
            case DashboardSideContent.DEEZER:
                // TODO: Handle Deezer click
                break;
            default:
                break;
        }
    };

    const getDaysAgo = (createdAt: string) => {
        const daysAgo = dayjs().diff(dayjs(createdAt), 'days')

        if (daysAgo <= 7) {
            if (daysAgo === 0) {
                return 'today'
            }
            return `${daysAgo} days ago`
        }

        return dayjs(createdAt).format('MMM D, YYYY')
    }

    const getCampaignTypeStat = (campaignType: string, analytics: any) => {
        switch (campaignType) {
            case PlaybookType.INCREASE_LINK_CLICKS:
                return {
                    statTitle: 'Cost per click',
                    statValue: analytics?.assetPerformanceAnalytics?.stats?.cpc || 0
                }
            case PlaybookType.INCREASE_MUSIC_STREAMS:
                return {
                    statTitle: 'Cost per click',
                    statValue: analytics?.assetPerformanceAnalytics?.stats?.cpc || 0
                }
            case PlaybookType.BOOST_INSTAGRAM_ENGAGEMENT:
                return {
                    statTitle: 'Instagram Followers',
                    statValue: analytics?.followers
                }
            case PlaybookType.INCREASE_VIDEO_VIEWS:
                return {
                    statTitle: 'Cost per view',
                    statValue: analytics?.assetPerformanceAnalytics?.stats?.cpv || 0
                }
            case PlaybookType.TIKTOK_BOOST_ENGAGEMENT:
                return {
                    statTitle: 'TikTok Views',
                    statValue: analytics?.views
                }
            case PlaybookType.META_PRESAVES:
                return {
                    statTitle: 'Pre-saves',
                    statValue: analytics?.totalOfPresaves || 0
                }
            default:
                return {
                    statTitle: 'Unknown',
                    statValue: 'Unknown'
                }
        }
    }

    const renderPageHeader = () => {
        return (
            <CardSymphony
                className="overallDashboardComponentCardTitle"
                title="Fanbase Growth"
                subtitle={
                    <div
                        className="overallDashboardComponentCardTitleSubtitle"
                        style={{
                            display: 'flex',
                            flexDirection: 'row'
                        }}
                    >
                        <div>
                            Track how your fanbase is growing across the board.
                        </div>
                    </div>
                }
                children={
                    <DefaultLearningIndicator
                        text="Learn how to use your Dashboard"
                        type={LearningIndicatorType.DASHBOARD}
                        style={{ marginBottom: 8 }}
                    />
                }
                isNoPadding
            />
        )
    }

    const renderGearIcon = () => {
        return (
            <SvgIcon viewBox="0 0 24 25"
                style={{ fontSize: 32 }}
            >
                <path fill="none" d="M12 15.0703C13.6569 15.0703 15 13.7272 15 12.0703C15 10.4135 13.6569 9.07031 12 9.07031C10.3431 9.07031 9 10.4135 9 12.0703C9 13.7272 10.3431 15.0703 12 15.0703Z" stroke={Colors.greyDark} strokeWidth="2" strokeLinecap="round" />
                <path fill="none" fillRule="evenodd" clipRule="evenodd" d="M10.069 3.43312C10.7151 1.61604 13.2849 1.61604 13.931 3.43311C14.338 4.57741 15.6451 5.11883 16.742 4.59744C18.4837 3.76949 20.3008 5.58656 19.4729 7.32834C18.9515 8.42522 19.4929 9.73234 20.6372 10.1393C22.4543 10.7854 22.4543 13.3552 20.6372 14.0013C19.4929 14.4083 18.9515 15.7154 19.4729 16.8123C20.3008 18.554 18.4837 20.3711 16.742 19.5432C15.6451 19.0218 14.338 19.5632 13.931 20.7075C13.2849 22.5246 10.7151 22.5246 10.069 20.7075C9.66203 19.5632 8.35491 19.0218 7.25803 19.5432C5.51625 20.3711 3.69918 18.554 4.52713 16.8123C5.04852 15.7154 4.5071 14.4083 3.3628 14.0013C1.54573 13.3552 1.54573 10.7854 3.36281 10.1393C4.5071 9.73234 5.04852 8.42522 4.52713 7.32834C3.69918 5.58656 5.51625 3.76949 7.25803 4.59744C8.35491 5.11883 9.66203 4.57741 10.069 3.43312Z" stroke={Colors.greyDark} strokeWidth="2" strokeLinecap="round" />
            </SvgIcon>
        )
    }

    function renderLargeGraphPopup() {
        return (
            <ModalSymphony
                className="overallDashboardLargeGraphPopup"
                isOpen={showLargeGraph && showLargeGraph.shown ? true : false}
                onClose={() => setShowLargeGraph({
                    ...showLargeGraph!,
                    shown: false
                })}
                style={{ zIndex: 998 }}
                maxWidth={900}
            >
                {showLargeGraph &&
                    <Fade in={showLargeGraph.shown ? true : false}>
                        <LargeGraphPopup
                            platformId={showLargeGraph.platformId}
                            platformName={showLargeGraph.platformName}
                            statName={showLargeGraph.statName}
                            statIdentifier={showLargeGraph.statIdentifier}
                            key={showLargeGraph.key}
                            imageAsset={showLargeGraph.imageAsset}
                            primaryValue={showLargeGraph.currentValue}
                            chartData={showLargeGraph.chartData}
                            changeValue={showLargeGraph.changeValue}
                            onClose={() => setShowLargeGraph({
                                ...showLargeGraph!,
                                shown: false
                            })}
                        />
                    </Fade>
                }
            </ModalSymphony>
        )
    }

    const renderOverallDashboard = () => {
        return (
            <>

                {!mediumView &&
                    renderPageHeader()
                }
                <div className="mx-auto relative overallDashboardComponent">
                    <OverallDashboard />
                </div>
                {mediumView &&
                    <div className="mt-4">
                        <DefaultLearningIndicator
                            text="Learn how to use your Dashboard"
                            type={LearningIndicatorType.DASHBOARD}
                        />
                    </div>
                }
            </>
        )
    }

    const renderSideContentInner = () => {
        return (
            <div className="dashboardSideItemsContainer" style={mStyle(sty, 'dashboardSideItemsContainer')}>
                {renderCampaignsRecent()}
            </div>
        )

        return (
            <div className="dashboardSideItemsContainer" style={mStyle(sty, 'dashboardSideItemsContainer')}>
                {dashboardSideContentArray.map((itemString, index) => {
                    const item = dashboardSideContent[itemString]
                    return (
                        <div
                            key={index}
                            className="dashboardSideItem"
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 4,
                                paddingBottom: 16,
                                borderBottom: index === dashboardSideContentArray.length - 1 ? 'none' : `1px solid ${Colors.divider}`,
                            }}
                        >
                            <img
                                className="dashboardSideItemImage"
                                style={sty.dashboardSideItemImage}
                                src={item.image}
                                alt={item.title}
                            />
                            <div
                                className="dashboardSideItemTitle"
                                style={{ fontSize: 18 }}
                            >
                                {item.title}
                            </div>
                            <div
                                className="dashboardSideItemDescriptionContainer"
                                style={sty.dashboardSideItemDescriptionContainer}
                                onClick={() => handleItemClick(itemString)}
                            >
                                <div
                                    className="dashboardSideItemDescription"
                                    style={sty.dashboardSideItemDescription}
                                >
                                    {item.description}
                                </div>
                                <img className="dashboardSideItemChevron" src={chevronRightGray} alt="chevron right" />
                            </div>
                        </div>
                    )
                })}
            </div>
        )
    }

    const renderUpdateNotifications = () => {
        return (
            <div className="updateNotificationsContainer" style={mStyle(sty, 'updateNotificationsContainer')}>
                <div style={{ marginRight: 'auto', marginBottom: 8 }}>Recommendations:</div>
                <Recommendations
                    isRecommdationVertical
                    isBanner={mediumView}
                />
                {/* <div className="updateNotificationsCount" style={sty.updateNotificationsCount}>
          {dashboardSideContentArray.length}
        </div> */}
            </div>
        )
    }

    const renderDashboardSideContent = () => {
        return (
            <div
                className="dashboardSideContentContainer"
                style={styx(
                    mStyle(sty, 'dashboardSideContentContainer'),
                    { maxHeight: cssCalc(48) }
                )}
            >
                <div className="sideDashboardButtonsContainer" style={mStyle(sty, 'sideDashboardButtonsContainer')}>
                    <ButtonSymphony
                        className="newWebsiteSideDashboardButton"
                        onClick={handleNewWebsite}
                        variant="outlined"
                        width="100%"
                        size="small"
                        iconLeft="plus"
                        iconLeftSize={18}
                        iconLeftOffset={-6}
                    >
                        New Website
                    </ButtonSymphony>
                    <ButtonSymphony
                        className="newCampaignSideDashboardButton"
                        onClick={() => setShowCreateNewCampaignModal(true)}
                        width="100%"
                        size="small"
                        iconLeft="plus"
                        iconLeftSize={18}
                        iconLeftOffset={-6}
                    >
                        New Campaign
                    </ButtonSymphony>
                </div>

                {/* {renderUpdateNotifications()} */}
                {renderSideContentInner()}
            </div>
        )
    }


    /** Handle closing the welcome modal */
    const handleCloseWelcomeModal = () => {
        setCookie('hasSeenWelcomeModal')
        setShowWelcomeModal(false)
    };

    const getDashboardLeftContainerWidth = () => {
        // if (!showRecommendationsSidebar || smallView || mediumView) {
        if (smallView || mediumView) {
            return '100%'
        }

        return 'calc(100% - 356px)'
    }

    const renderPlatformGraphs = () => {
        var lastDaysString = "Last 7 Days"
        var interval: any = "preserveEnd"
        if (timeRange === 'last-30-days') {
            lastDaysString = "Last 30 Days"
            interval = 6
        } else if (timeRange === "last-3-months") {
            lastDaysString = "Last 3 Months"
            interval = 9
        } else if (timeRange === 'custom') {
            interval = "preserveStart"
            lastDaysString = `${dayjs(selectedDate.startDate).format('M/D')} to ${dayjs(selectedDate.endDate).format('M/D')}`
        }

        if (window.innerWidth < 576) {
            interval = "preserveEnd"
        }

        const graphData = platformGraphsData.map((platformData: GraphPresentation, idx: number) => {
            const {
                platformId,
                platformName,
                chartData,
                currentValue,
                changeValue,
                imageAsset,
                statName,
                isConnected,
                decommissionMessage = null
            } = platformData

            // NOTE: we are not supporting the platform anymore
            if (isConnected && decommissionMessage) {
                return null
            }

            if (!isConnected) {
                return (
                    <div
                        key={idx}
                        className="overallDashboardPlatformCard"
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden',
                            width: '100%',
                            position: 'relative',
                            flex: 1,
                            border: Sty.border,
                            borderRadius: 8,
                        }}
                    >
                        <div
                            className="imageAssetOuterContainer"
                            style={{
                                display: 'flex',
                                flexDirection: 'row',
                                gap: 8,
                                paddingTop: 8,
                                paddingLeft: 16,
                                paddingRight: 16,
                                paddingBottom: 8,
                            }}
                        >
                            <img
                                className="imageDashboardAssetIcon"
                                style={{ flexShrink: 0 }}
                                width={50}
                                src={imageAsset}
                                alt="imageDashboardAssetIcon"
                            />
                            <div className="platformInfoContainer">
                                <p className="platformInfoTitle" style={Sty.text16bold}>
                                    Track {platformName} {statName}
                                </p>
                                <p className="platformInfoSubtitle" style={Sty.text12grey}>
                                    Connect your {platformName} account to track your {statName}.
                                </p>
                            </div>
                        </div>
                        {isBrandAdmin &&
                            <ButtonSymphony
                                className="overallDashboardConnectButton"
                                onClick={() => setSelectedPlatformForConnect(platformId as SocialConnectType)}
                                variant="outlined"
                                style={{
                                    marginTop: 8,
                                    marginBottom: 16,
                                    alignSelf: 'center'
                                }}
                            >
                                Connect
                            </ButtonSymphony>
                        }
                    </div>
                )
            }

            return (
                <SmallGraph
                    key={idx}
                    index={idx}
                    expandGraph={() => setShowLargeGraph(platformData)}
                    platformName={platformName}
                    imageAsset={imageAsset}
                    primaryValue={currentValue && millify(currentValue)}
                    subtitle={statName}
                    changeValue={changeValue ? changeValue : 0}
                    graphData={chartData}
                />
            )
        })

        return (
            <>
                {mediumView && renderPlaybooks()}
                <div
                    className="overallDashboardPlatformsContainer"
                    style={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        marginTop: 24
                    }}
                >
                    <div className="dashboardPlatformsHeaderContainer" style={{ paddingRight: 16 }}>
                        <div style={Sty.text20}>
                            Platforms ({lastDaysString})
                        </div>
                        <div style={Sty.text16grey}>
                            The total change in fans {timeRange === 'custom' ? 'from ' : 'over the '} {lastDaysString.toLowerCase()}.
                        </div>
                    </div>
                    {isBrandAdmin &&
                        <ButtonSymphony
                            className="overallDashboardEditGearButton"
                            onClick={() => setShowSocialConnectModal(true)}
                            aria-label="edit"
                            variant="text"
                            iconRight={renderGearIcon()}
                            color="grey"
                        >
                            Edit
                        </ButtonSymphony>
                    }
                </div>
                <div
                    className="overallDashboardPlatformsCardsContainer"
                    style={{
                        display: 'grid',
                        gridTemplateColumns: mediumView ? '1fr' : 'repeat(2, 1fr)',
                        gap: 8,
                        marginTop: 16,
                        width: '100%',
                    }}
                >
                    {graphData}
                </div>
            </>
        )
    }

    const handleTikTokAuth = async (authCode: string) => {
        setIsTikTokConnecting(true);
        setTikTokError(null);

        try {
            await connectToBrand_api({
                brandId: currentBrand?.id,
                brandSlug: currentBrand?.slug,
                value: { code: authCode },
                service: 'tiktok_auth'
            });

            // Clear auth code and close modal on success
            localStorage.removeItem('tt_auth_code');
            setShowTikTokAuthModal(false);

            // Close the popup window
            if (window.opener) {
                window.opener.close();
            }
        } catch (error) {
            console.error('Error connecting TikTok:', error);
            setTikTokError('Failed to connect your TikTok account. Please try again.');
        } finally {
            setIsTikTokConnecting(false);
        }
    }

    const renderPlaybooks = () => {
        return (
            <div
                className="playbooksContainer"
                style={{
                    width: '100%',
                    paddingTop: mediumView ? 16 : 0
                }}
            >
                <div
                    className="playbooksTitle"
                    style={{
                        ...Sty.text18,
                        paddingBottom: 8
                    }}
                >
                    Playbooks
                </div>
                <div
                    className="playbooksScrollContainer"
                    style={{
                        display: 'flex',
                        flexDirection: 'row',
                        gap: 8,
                        overflowX: 'scroll',
                        width: '100%',
                        scrollbarWidth: 'none',
                    }}
                >
                    {PLAYBOOKS_FOR_DASHBOARD
                        .filter(playbook => {
                            // If the playbook is only for Symphony Insiders, check if the user is a Symphony Insider
                            if (playbook.onlyForSymphonyBetaTestUsers) {
                                return isSymphonyBetaTestUser;
                            }
                            if (playbook.hidden) {
                                return false;
                            }
                            return true;
                        })
                        .map((playbook, index) => {
                            const {
                                name,
                                icon,
                                title,
                                background,
                                onClick,
                            } = playbook;

                            return (
                                <div
                                    key={makeKey(name, index)}
                                    className="playbookCard"
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 4,
                                        borderRadius: 8,
                                        paddingTop: 12,
                                        paddingLeft: 8,
                                        paddingRight: 8,
                                        paddingBottom: 8,
                                        alignItems: 'flex-start',
                                        flexShrink: 0,
                                        scrollbarWidth: 'none',
                                        background,
                                        width: 150,
                                        height: 120,
                                    }}
                                >
                                    <div
                                        className="playbookCardImageContainer"
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'row',
                                            alignItems: 'flex-start',
                                            gap: 8,
                                        }}
                                    >
                                        <Icon
                                            className="playbookCardSocialIcon"
                                            icon={icon}
                                            color="white"
                                            size={20}
                                        />
                                        <div
                                            className="playbookCardTitle"
                                            style={{
                                                ...Sty.text16,
                                                color: 'white',
                                                whiteSpace: 'break-spaces',
                                            }}
                                        >
                                            {title}
                                        </div>
                                    </div>

                                    <ButtonSymphony
                                        className="playbookButton"
                                        style={{
                                            marginTop: 'auto',
                                            paddingLeft: 10,
                                            paddingRight: 10,
                                            backgroundColor: 'rgba(255, 255, 255, 0.5)',
                                            marginLeft: 'auto',
                                            marginRight: 'auto',
                                            color: "white"
                                        }}
                                        size="small"
                                        onClick={onClick}
                                        color="white"

                                        iconLeft="stars"
                                        iconLeftOffset={-3}
                                        iconLeftSize={16}
                                        iconLeftColor="white"

                                        iconRight="chevronRight"
                                        iconRightOffset={-3}
                                        iconRightSize={14}
                                        iconRightColor="white"
                                    >
                                        Get started
                                    </ButtonSymphony>
                                </div>
                            )
                        })}
                </div>
            </div>
        )
    }

    const renderCampaignsRecent = () => {
        if (mediumView) {
            return null;
        }

        const hasNoCampaigns = campaignsRecent.length === 0;
        const title = hasNoCampaigns ? "Get Started" : "Recent Campaigns";
        return (
            <div className="activeCampaignsContainer">
                <div
                    className="activeCampaignsTitle"
                    style={{
                        ...Sty.text18,
                        paddingLeft: mediumView ? 16 : 0,
                        paddingRight: 16,
                        paddingBottom: 8,
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: 8
                    }}
                >
                    <div className="recentCampaignsTitle">{title}</div>
                    {isLoadingCampaignsRecent &&
                        <Loader
                            className="activeCampaignsLoader"
                        />
                    }
                </div>
                <div
                    className="activeCampaignsContainer"
                    style={mStyle(sty, 'activeCampaignsContainer')}
                >
                    {hasNoCampaigns &&
                        <Recommendations
                            isRecommdationVertical
                        />
                    }

                    {campaignsRecent.map((campaign, index) => {
                        const {
                            status,
                            campaign_metadata,
                            analytics,
                            createdAt
                        } = campaign;

                        const {
                            content,
                            campaign_type,
                            playbook,
                            v2_campaign_input
                        } = campaign_metadata || {};

                        const campaignName = content?.name || v2_campaign_input?.campaignName || 'Untitled Campaign';
                        const campaignType = playbook || campaign_type || 'Unknown';
                        const campaignStatus = status as CampaignStatus || CampaignStatus.UNKNOWN;

                        const {
                            statTitle,
                            statValue
                        } = getCampaignTypeStat(campaignType, analytics);

                        return (
                            <div
                                key={makeKey(campaign.id, index)}
                                className="activeCampaignCard"
                                style={mStyle(sty, 'activeCampaignCard')}
                            >
                                {/* <DebugBox
                                    className="activeCampaignCardDebugBox"
                                    alwaysShow
                                    data={campaign}
                                /> */}
                                <CampaignImageSymphony
                                    className="activeCampaignCardThumbnail"
                                    style={{
                                        width: '100%',
                                        height: mediumView ? 50 : 100,
                                        borderRadius: 5,
                                    }}
                                    campaignMetadata={campaign_metadata}
                                    isBackgroundImage={true}
                                    iconSize={60}
                                />

                                <div
                                    className="activeCampaignCardTitle"
                                    style={mediumView ?
                                        Sty.text12grey
                                        :
                                        { ...Sty.text16, marginTop: 2 }
                                    }
                                >
                                    {getFallbackCampaignName(campaign_metadata)}
                                </div>
                                <div className="activeCampaignCardSubtitle" style={Sty.text14grey}>
                                    Created {getDaysAgo(createdAt)}
                                </div>


                                {/* <div>
                                    {statTitle}
                                </div>
                                <div style={{ color: Colors.green, fontSize: 16, fontWeight: 600 }}>
                                    {statValue}
                                </div> */}

                                <TagSymphony
                                    className="activeCampaignTag"
                                    icon={getCampaignTypeInfo(campaignType).icon}
                                    title={getCampaignTypeInfo(campaignType).title}
                                    type="square"
                                    size={mediumView ? "small" : "medium"}
                                />
                                <TagSymphony
                                    className="activeCampaignTag"
                                    title={CAMPAIGN_STATUS_INFO[campaignStatus].title}
                                    color={CAMPAIGN_STATUS_INFO[campaignStatus].color}
                                    type="square"
                                    size={mediumView ? "small" : "medium"}
                                />
                                <div
                                    className="activeCampaignButtonsContainer"
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'row',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        gap: 8,
                                        width: '100%'
                                    }}
                                >
                                    <ButtonSymphony
                                        className="activeCampaignEditButton"
                                        onClick={() => {
                                            navigator.clipboard.writeText(`${window.location.origin}/marketing/campaigns/${campaign.id}`)
                                            toastSuccess('Link copied to clipboard')
                                        }}
                                        variant="text"
                                        size={mediumView ? "small" : "medium"}
                                        iconRight="link2"
                                        iconRightSize={mediumView ? 12 : 16}
                                        iconRightOffset={-2}
                                        tooltip="Copy to clipboard"
                                    >
                                        Copy Link
                                    </ButtonSymphony>
                                    <ButtonSymphony
                                        className="activeCampaignViewButton"
                                        variant="text"
                                        size={mediumView ? "small" : "medium"}
                                        iconRight="arrowRight"
                                        iconRightSize={mediumView ? 16 : 20}
                                        iconRightOffset={-8}
                                        onClick={() => {
                                            history.push(`/marketing/campaigns/${campaign.id}`)
                                        }}
                                    >
                                        View
                                    </ButtonSymphony>
                                </div>
                            </div>
                        )
                    })}
                </div>
            </div>
        )
    }

    const renderPageContent = () => {
        let primaryContent;
        if (isLoadingInitialData) {
            primaryContent = (
                <Loader size={44} title="Loading Dashboard..." isCentered />
            )
        } else {
            primaryContent = (
                <PageSymphony
                    className="dashboardComponent"
                    isProMobileNavCta
                >
                    <ContentSymphony>
                        {/* Only render BannerContainer if at least one banner needs to be shown */}
                        <div className="dashboardInnerContainer" style={mStyle(sty, 'dashboardInnerContainer')}>
                            <div
                                className="dashboardInnerContainerLeft"
                                style={styx(
                                    mStyle(sty, 'dashboardInnerContainerLeft'),
                                    { width: getDashboardLeftContainerWidth() }
                                )}
                            >
                                {!mediumView && renderPlaybooks()}
                                {mediumView && renderCampaignsRecent()}
                                {mediumView && showRecommendationsSidebar &&
                                    <Recommendations isBanner />
                                }
                                <div
                                    className="overallDashboardContainer"
                                    style={mStyle(sty, 'overallDashboardContainer')}
                                >
                                    {renderOverallDashboard()}
                                    {renderPlatformGraphs()}
                                </div>
                            </div>
                            {!mediumView && showRecommendationsSidebar &&
                                renderDashboardSideContent()
                            }
                            {!mediumView && SHOW_ACTIVE_CAMPAIGNS &&
                                renderDashboardSideContent()
                            }
                        </div>
                    </ContentSymphony>
                </PageSymphony>
            )
        }

        return primaryContent
    }

    // NOTE: render()
    return (
        <>
            {renderPageContent()}

            {/* RENDER: MODALS */}
            <PlatformsConnector
                isOpen={showSocialConnectModal}
                closeModal={() => setShowSocialConnectModal(false)}
            />

            {currentBrand &&
                <UpgradeToProSucceededModal
                    dialogProps={{
                        open: showUpgradeToProSucceededModal,
                        onClose: closeUpgradeToProSucceededModal,
                    }}
                    sessionId={sessionId}
                />
            }

            {/* NOTE: modals */}
            {renderLargeGraphPopup()}

            {selectedPlatformForConnect &&
                <SocialConnectModal
                    className="socialConnectModalOverallDashboard"
                    isOpen={!!selectedPlatformForConnect}
                    onClose={() => setSelectedPlatformForConnect(null)}
                    socialConnectType={selectedPlatformForConnect}
                    validateBusinessAccount
                />
            }

            <ModalSymphony
                className="welcomeToSymphonyModal"
                isOpen={showWelcomeModal}
                onClose={handleCloseWelcomeModal}
                maxWidth={750}
            >
                <WelcomeToSymphony skipAction={handleCloseWelcomeModal} />
            </ModalSymphony>

            <ModalSymphony
                className="tiktokAuthModal"
                isOpen={showTikTokAuthModal}
                onClose={() => {
                    setShowTikTokAuthModal(false);
                    localStorage.removeItem('tt_auth_code');
                }}
                maxWidth={400}
            >
                <div style={{ padding: 24, textAlign: 'center' }}>
                    {isTikTokConnecting ? (
                        <>
                            <Loader />
                            <p style={{ marginTop: 16 }}>Connecting your TikTok account...</p>
                        </>
                    ) : tikTokError ? (
                        <>
                            <p style={{ color: Colors.red }}>{tikTokError}</p>
                            <ButtonSymphony
                                className="tiktokAuthModalCloseButton"
                                onClick={() => {
                                    setShowTikTokAuthModal(false);
                                    localStorage.removeItem('tt_auth_code');
                                }}
                                style={{ marginTop: 16 }}
                            >
                                Close
                            </ButtonSymphony>
                        </>
                    ) : (
                        <p>TikTok connection completed successfully!</p>
                    )}
                </div>
            </ModalSymphony>
        </>
    );
};

const sty: Record<string, CSSProperties> = {
    dashboardComponent: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
        paddingTop: 24,
        paddingLeft: 24,
        paddingRight: 24,
        paddingBottom: 24,
        marginBottom: 0,
    },
    dashboardComponent_medium: {
        paddingLeft: 0,
        paddingRight: 0,
        paddingTop: 0,
        paddingBottom: 0,
    },
    overallDashboardContainer: {
        backgroundColor: 'white',
        paddingLeft: 24,
        paddingRight: 24,
        paddingTop: 24,
        paddingBottom: 24,
        width: '100%',
        maxWidth: 1200,
        borderRadius: 8,
    },
    overallDashboardContainer_mobile: {
        paddingLeft: 16,
        paddingRight: 16,
        paddingTop: 16,
        paddingBottom: 16,
        borderRadius: 0,
    },
    overallDashboardContainer_medium: {
        paddingLeft: 16,
        paddingRight: 16,
        paddingTop: 16,
        paddingBottom: 16,
    },
    dashboardInnerContainer: {
        display: 'flex',
        flexDirection: 'row',
        gap: 16,
        maxWidth: 1200,
        width: '100%',
    },
    dashboardInnerContainer_medium: {
        flexDirection: 'column',
    },
    dashboardInnerContainerLeft: {
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
    },
    dashboardSideContentContainer: {
        position: 'sticky',
        top: 24,
        backgroundColor: 'white',
        padding: 16,
        width: 340,
        borderRadius: 8,
        flexShrink: 0,
        alignSelf: 'flex-start',
        overflowY: 'scroll',
        scrollbarWidth: 'none',
    },
    dashboardSideContentContainer_medium: {
        marginLeft: 'auto',
        marginRight: 'auto',
        marginBottom: 32,
    },
    sideDashboardButtonsContainer: {
        position: 'sticky',
        top: -16,
        backgroundColor: 'white',
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 8,
        marginTop: -16,
        marginBottom: 16,
        paddingTop: 16,
        paddingBottom: 16,
        borderBottom: Sty.border,
    },
    dashboardSideItemsContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: 24,
    },
    dashboardSideItemImage: {
        width: '100%',
        height: 120,
        borderRadius: 12,
    },
    dashboardSideItemDescription: {
        color: Colors.greyDark,
        display: '-webkit-box',
        cursor: 'pointer',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        lineHeight: '1.2em',
        maxHeight: '2.4em',
    },
    dashboardSideItemDescriptionContainer: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 8,
    },
    updateNotificationsCount: {
        paddingTop: 1,
        paddingBottom: 1,
        paddingLeft: 9,
        paddingRight: 9,
        backgroundColor: Colors.purple,
        color: 'white',
        borderRadius: 8,
        width: 'fit-content',
    },
    updateNotificationsContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'space-between',
    },

    activeCampaignsContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        overflow: 'scroll',
        paddingLeft: 0,
        paddingRight: 0,
    },
    activeCampaignsContainer_medium: {
        flexDirection: 'row',
        overflow: 'scroll',
        paddingLeft: 16,
        paddingRight: 16,
    },
    activeCampaignCard: {
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        padding: 8,
        backgroundColor: 'white',
        borderRadius: 8,
        alignItems: 'flex-start',
        width: '100%',
        flexShrink: 0,
        scrollbarWidth: 'none',
        border: Sty.border,
    },
    activeCampaignCard_medium: {
        width: 160,
        border: 'none',
        padding: 4,
    },
}

export default Dashboard;